import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

const PrintSettings = () => {
  const [receiptSettings, setReceiptSettings] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [message, setMessage] = useState('');

  const { storeId } = useSelector((state) => state.user);
  const apiBase = 
  process.env.REACT_APP_BASEURL ||
   window.__APP_CONFIG__?.API_BASE_URL || "";
  const BASE_URL = apiBase;

  // Fetch receipt settings on component mount
  useEffect(() => {
    fetchPrintSettings();
  }, [storeId]);

  const fetchPrintSettings = async () => {
    setFetchingData(true);
    setMessage('');
    try {
      const response = await axios.get(`${BASE_URL}api/v1/Admin/GetReceiptSetting`, {
        params: { storeId }
      });

      if (response.data && Array.isArray(response.data.data)) {
        const fetchedReceipts = response.data.data;
        setReceiptSettings(fetchedReceipts);

        // Initialize settings object with receipt data
        const initialSettings = {};
        fetchedReceipts.forEach(receipt => {
          // Use receipt id as the key for settings
          initialSettings[receipt.id] = receipt.isActive || false;
        });

        setSettings(initialSettings);
      } else {
        setReceiptSettings([]);
        setSettings({});
      }
    } catch (error) {
      console.error("Error fetching receipt settings:", error);
      setMessage('Error loading receipt settings. Please try again.');
      setReceiptSettings([]);
      setSettings({});
    } finally {
      setFetchingData(false);
    }
  };

  const handleToggle = (receiptId) => {
    setSettings(prev => ({
      ...prev,
      [receiptId]: !prev[receiptId]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage('');

    try {
      // Prepare payload as an array of receipt settings matching API requirement
      const payload = receiptSettings.map(receipt => ({
        receiptName: receipt.receiptName,
        isActive: settings[receipt.id] !== undefined ? settings[receipt.id] : receipt.isActive
      }));

      const response = await axios.post(
        `${BASE_URL}api/v1/Admin/SaveReceiptSettings`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Check for successful response
      if (response.status === 200 || response.status === 201) {
        setMessage('Settings saved successfully!');
        // Refresh the data after save to get updated values
        setTimeout(() => {
          fetchPrintSettings();
        }, 1000);
      } else {
        setMessage('Failed to save settings. Please try again.');
      }
    } catch (error) {
      console.error('Error saving print settings:', error);

      // Provide more specific error messages
      if (error.response) {
        const errorMsg = error.response.data?.message || error.response.data?.error || 'Server error occurred';
        setMessage(`Error: ${errorMsg}`);
      } else if (error.request) {
        setMessage('No response from server. Please check your connection.');
      } else {
        setMessage('Error saving settings. Please try again.');
      }
    } finally {
      setLoading(false);

      // Clear message after 4 seconds
      setTimeout(() => {
        setMessage('');
      }, 4000);
    }
  };

  if (fetchingData) {
    return (
      <div className="print-settings-container">
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading receipt settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="print-settings-container">
      <div className="card">
        {/* Message */}
        {message && (
          <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-danger'} mt-3 mx-3 mb-0`}>
            {message}
          </div>
        )}
        <div className="card-header">
          <h5 className="card-title mb-0">Print Receipt Settings</h5>
          <p className="text-muted mb-0 small mt-1">Configure which receipts should be printed</p>
        </div>
        <div className="card-body">
          {receiptSettings.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No receipt settings found. Please contact administrator.</p>
            </div>
          ) : (
            <>
              {/* Dynamic Receipt Settings */}
              {receiptSettings.map((receipt) => (
                <div key={receipt.id} className="setting-item mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <h6 className="mb-1"> {receipt.receiptName} </h6>
                      <p className="text-muted mb-0 small">
                        Enable or disable {receipt.receiptName.toLowerCase()} receipt printing
                      </p>
                    </div>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        className="toggle-checkbox"
                        id={`receipt-${receipt.id}`}
                        checked={settings[receipt.id] || false}
                        onChange={() => handleToggle(receipt.id)}
                      />
                      <label className="toggle-label" htmlFor={`receipt-${receipt.id}`}>
                        <span className="toggle-inner"></span>
                      </label>
                      <div className="toggle-text">
                        {settings[receipt.id] ? 'Yes' : 'No'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Save Button */}
              <div className="d-flex justify-content-end mt-4">
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
  .print-settings-container {
    margin: 0 auto;
  }
  
  .setting-item {
    padding: 15px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #fafafa;
    transition: background-color 0.2s ease;
  }
  
  .setting-item:hover {
    background-color: #f5f5f5;
  }
  
  .toggle-switch {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  
  .toggle-checkbox {
    display: none;
  }
  
  .toggle-label {
    display: inline-block;
    width: 38px;  /* Reduced from 44px */
    height: 20px; /* Reduced from 24px */
    background-color: #ccc;
    border-radius: 10px; /* Reduced from 12px */
    position: relative;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  .toggle-checkbox:checked + .toggle-label {
    background-color: #008080;
  }
  
  .toggle-label:hover {
    opacity: 0.9;
  }
  
  .toggle-inner {
    display: block;
    width: 14px; /* Reduced from 18px */
    height: 14px; /* Reduced from 18px */
    background-color: white;
    border-radius: 50%;
    position: absolute;
    top: 3px;
    left: 3px;
    transition: transform 0.3s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
  
  .toggle-checkbox:checked + .toggle-label .toggle-inner {
    transform: translateX(18px); /* Adjusted for smaller width */
  }
  
  .toggle-text {
    font-weight: 600;
    color: #333;
    min-width: 30px;
    font-size: 0.875rem;
  }
  
  .card {
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    border: none;
    border-radius: 10px;
  }
  
  .card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
    padding: 15px 20px;
    border-radius: 10px 10px 0 0;
  }
  
  .card-body {
    padding: 20px;
  }
  
  .btn-primary {
    background-color: #008080;
    border-color: #008080;
    padding: 8px 24px;
    font-weight: 500;
  }
  
  .btn-primary:hover {
    background-color: #006666;
    border-color: #006666;
  }
  
  .btn-primary:disabled {
    background-color: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
  }
  
  .alert {
    border-radius: 8px;
    padding: 12px 20px;
  }
  
  .spinner-border-sm {
    width: 1rem;
    height: 1rem;
    border-width: 0.15em;
  }
`}</style>
    </div>
  );
};

export default PrintSettings;